import { useState } from "react";
import { Badge, Button, Code, Kbd, SegmentedControl, Tooltip } from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";
import { ArrowRightIcon, PlusIcon, TrashIcon } from "../ui/icons";

const VARIANTS = [
  "primary",
  "secondary",
  "glass",
  "floating",
  "subtle",
  "outline",
  "ghost",
  "danger",
  "link",
] as const;
const SIZES = ["xs", "sm", "md", "lg"] as const;
const RELIEFS = ["none", "subtle", "medium", "strong"] as const;
const TONES = ["neutral", "brand", "success", "warning", "danger", "info", "solid"] as const;

type Relief = (typeof RELIEFS)[number];

export function ButtonSection() {
  const [loading, setLoading] = useState(false);
  const [relief, setRelief] = useState<Relief>("subtle");

  const simulate = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1400);
  };

  return (
    <Section
      id="button"
      eyebrow="Actions"
      title="Button"
      description="Neuf variantes, quatre niveaux de relief. L'appui est rendu par brightness() et opacity — jamais par un scale, qui ferait vibrer la mise en page."
    >
      <SubSection title="Variantes" hint="Le relief se règle ici pour toute la section">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-raised/40 p-4">
          <span className="text-xs text-fg-muted">
            Prop <Code>relief</Code> — défaut <Code>subtle</Code>
          </span>
          <SegmentedControl
            aria-label="Intensité du relief"
            size="sm"
            value={relief}
            onValueChange={(value) => setRelief(value as Relief)}
            options={RELIEFS.map((value) => ({ value, label: value }))}
          />
        </div>

        <Preview
          label="button.tsx"
          code={`<Button variant="primary" relief="${relief}">primary</Button>
<Button variant="secondary" relief="${relief}">secondary</Button>
<Button variant="glass" relief="${relief}">glass</Button>
<Button variant="ghost">ghost</Button>
<Button variant="danger" relief="${relief}">danger</Button>`}
        >
          {VARIANTS.map((variant) => (
            <Button key={variant} variant={variant} relief={relief}>
              {variant}
            </Button>
          ))}
        </Preview>
      </SubSection>

      <SubSection title="Icônes & badges">
        <Preview
          label="toolbar.tsx"
          code={`<Button leadingIcon={<PlusIcon />}>Nouveau projet</Button>
<Button variant="secondary" leadingIcon={<PlusIcon />} badge="12">Messages</Button>
<Button variant="glass" leadingIcon={<ArrowRightIcon />} pill>Capsule</Button>
<Button variant="subtle" trailingIcon={<ArrowRightIcon />}>Continuer</Button>

<Button size="icon" variant="ghost" aria-label="Supprimer"><TrashIcon /></Button>`}
        >
          <Button variant="primary" relief={relief} leadingIcon={<PlusIcon />}>
            Nouveau projet
          </Button>
          <Button variant="secondary" relief={relief} leadingIcon={<PlusIcon />} badge="12">
            Messages
          </Button>
          <Button variant="glass" relief={relief} leadingIcon={<ArrowRightIcon />} pill>
            Capsule
          </Button>
          <Button variant="subtle" relief={relief} trailingIcon={<ArrowRightIcon />}>
            Continuer
          </Button>
          <Button size="icon-sm" variant="secondary" relief={relief} aria-label="Ajouter">
            <PlusIcon />
          </Button>
          <Button size="icon" variant="glass" relief={relief} aria-label="Ajouter">
            <PlusIcon />
          </Button>
          <Button size="icon" variant="ghost" aria-label="Supprimer">
            <TrashIcon />
          </Button>
        </Preview>
      </SubSection>

      <SubSection title="Tailles">
        <Preview
          label="sizes.tsx"
          code={`{(["xs", "sm", "md", "lg"] as const).map((size) => (
  <Button key={size} size={size} variant="secondary">Taille {size}</Button>
))}

<Button pill>Capsule</Button>`}
        >
          {SIZES.map((size) => (
            <Button key={size} size={size} variant="secondary" relief={relief}>
              Taille {size}
            </Button>
          ))}
          <Button variant="primary" relief={relief} pill>
            Capsule
          </Button>
        </Preview>
      </SubSection>

      <SubSection title="États">
        <Preview
          label="states.tsx"
          code={`<Button loading={loading} onClick={save}>Enregistrer</Button>
<Button disabled>Désactivé</Button>

<Tooltip content="Cette action est irréversible">
  <Button variant="danger" leadingIcon={<TrashIcon />}>Supprimer</Button>
</Tooltip>`}
        >
          <Button loading={loading} onClick={simulate} variant="secondary" relief={relief}>
            {loading ? "Enregistrement…" : "Cliquer pour charger"}
          </Button>
          <Button disabled variant="secondary" relief={relief}>
            Désactivé
          </Button>
          <Tooltip content="Cette action est irréversible">
            <Button variant="danger" relief={relief} leadingIcon={<TrashIcon />}>
              Supprimer
            </Button>
          </Tooltip>
        </Preview>
      </SubSection>
    </Section>
  );
}

export function BadgeSection() {
  return (
    <Section
      id="badge"
      eyebrow="Actions"
      title="Badge, Kbd & Code"
      description="Le badge est informatif. S'il doit être cliquable, c'est un bouton."
    >
      <SubSection title="Tons">
        <Preview
          label="badge.tsx"
          code={`<Badge tone="neutral">neutral</Badge>
<Badge tone="brand">brand</Badge>
<Badge tone="success">success</Badge>
<Badge tone="danger">danger</Badge>`}
        >
          {TONES.map((tone) => (
            <Badge key={tone} tone={tone}>
              {tone}
            </Badge>
          ))}
        </Preview>
      </SubSection>

      <SubSection title="Pastille & forme">
        <Preview
          label="status.tsx"
          code={`<Badge tone="success" dot pulse>En direct</Badge>
<Badge tone="success" dot>En ligne</Badge>
<Badge tone="danger" dot>Hors service</Badge>
<Badge tone="brand" shape="rounded" size="md">Carré</Badge>`}
        >
          <Badge tone="success" dot pulse>
            En direct
          </Badge>
          <Badge tone="success" dot>
            En ligne
          </Badge>
          <Badge tone="warning" dot>
            Dégradé
          </Badge>
          <Badge tone="danger" dot>
            Hors service
          </Badge>
          <Badge tone="brand" shape="rounded" size="md">
            Carré, taille md
          </Badge>
        </Preview>
      </SubSection>

      <SubSection title="Kbd & Code">
        <Preview
          label="inline.tsx"
          code={`Ouvrir la palette : <Kbd>⌘</Kbd> <Kbd>K</Kbd>

Installer avec <Code>pnpm add @one-nexty/components-web</Code>`}
        >
          <span className="text-sm text-fg-muted">
            Ouvrir la palette : <Kbd>⌘</Kbd> <Kbd>K</Kbd>
          </span>
          <span className="text-sm text-fg-muted">
            Installer avec <Code>pnpm add @one-nexty/components-web</Code>
          </span>
        </Preview>
      </SubSection>
    </Section>
  );
}
